import type { CommissionLine, CommissionPolicyIn } from '../../shared/api/generated/model'

export type RateKey = keyof Omit<CommissionPolicyIn, 'effective_from'>

export const rateFields: { key: RateKey; label: string }[] = [
  { key: 'solo_sale_bps', label: 'Individual · venda' },
  { key: 'solo_operated_bps', label: 'Individual · operação' },
  { key: 'shared_originator_sale_bps', label: 'Originador · venda compartilhada' },
  { key: 'shared_originator_operated_bps', label: 'Originador · operação compartilhada' },
  { key: 'shared_closer_sale_bps', label: 'Fechador · venda compartilhada' },
  { key: 'shared_closer_operated_bps', label: 'Fechador · operação compartilhada' },
]

/** Os mesmos seis campos como matriz papel × parcela, para o formulário. */
export const rateMatrix: { role: string; hint: string; sale: RateKey; operated: RateKey }[] = [
  {
    role: 'Individual',
    hint: 'Originou e fechou',
    sale: 'solo_sale_bps',
    operated: 'solo_operated_bps',
  },
  {
    role: 'Originador',
    hint: 'Venda compartilhada',
    sale: 'shared_originator_sale_bps',
    operated: 'shared_originator_operated_bps',
  },
  {
    role: 'Fechador',
    hint: 'Venda compartilhada',
    sale: 'shared_closer_sale_bps',
    operated: 'shared_closer_operated_bps',
  },
]

export const issueLabels: Record<string, string> = {
  missing_originator: 'Informar originador',
  missing_closer: 'Informar fechador',
  missing_sale_date: 'Informar data da venda',
  missing_surgery_date: 'Cirurgia operada sem data',
  missing_current_contract: 'Contrato vigente não identificado',
  ambiguous_current_contract: 'Conferir contrato vigente',
  inconsistent_contract_items: 'Conferir itens do contrato vigente',
  missing_medical_fees: 'Honorários não identificados',
  ambiguous_addendum_base: 'Conferir base dos aditivos',
  missing_agent_profile: 'Cadastrar agente comercial',
  missing_policy: 'Configurar percentual vigente',
  inactive_agent: 'Agente inativo na venda',
}

export const participationLabels: Record<string, string> = {
  solo: 'Individual',
  originator: 'Originador',
  closer: 'Fechador',
}

export const triggerLabels: Record<CommissionLine['trigger'], string> = {
  sale_closed: 'Venda',
  surgery_operated: 'Operação',
}

// "Apurada" é reconhecimento, não pagamento: não usa verde (ui/DESIGN.md).
export const statusMeta: Record<string, { label: string; tone: 'neutral' | 'info' | 'warn' }> = {
  earned: { label: 'Apurada', tone: 'neutral' },
  processing: { label: 'Pronta para apurar', tone: 'info' },
  pending: { label: 'Pendente', tone: 'warn' },
}

export const money = (cents: number | null | undefined) =>
  cents == null
    ? '—'
    : (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export const percent = (bps: number | null | undefined) =>
  bps == null ? '—' : `${(bps / 100).toLocaleString('pt-BR')}%`

export const shortDate = (value: string | null | undefined) =>
  value
    ? new Date(`${dateInClinic(value)}T12:00:00-03:00`).toLocaleDateString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
      })
    : '—'

export function dateInClinic(value: string | null | undefined): string {
  if (!value) return ''
  if (value.length === 10) return value
  // API timestamps without an offset are stored in UTC.
  const timestamp = /(?:Z|[+-]\d\d:\d\d)$/.test(value) ? value : `${value}Z`
  return new Date(timestamp).toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' })
}

export const currentMonth = () =>
  new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' }).slice(0, 7)

/** "2026-08" → "Agosto de 2026" */
export function competenceLabel(competence: string): string {
  const [year, month] = competence.split('-').map(Number)
  if (!year || !month) return competence
  const label = new Date(Date.UTC(year, month - 1, 15)).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

/** Desloca uma competência "YYYY-MM" em N meses. */
export function shiftMonth(competence: string, delta: number): string {
  const [year, month] = competence.split('-').map(Number)
  if (!year || !month) return competence
  const d = new Date(Date.UTC(year, month - 1 + delta, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

export const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

export function errorMessage(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string'
    ? detail
    : 'Não foi possível concluir. Confira os dados e tente novamente.'
}

export function percentToBps(value: string): number | null {
  const normalized = value.trim().replace(',', '.')
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(normalized)) return null
  const [integer, decimal = ''] = normalized.split('.')
  const bps = Number(integer) * 100 + Number(decimal.padEnd(2, '0'))
  return bps <= 10000 ? bps : null
}
